import { gql, useMutation } from "@apollo/client";
import { useGetTelehealthSessionInfo } from "../../hooks";
import { useVisitorId } from "../useVisitorId";
import { useCallback } from "react";
import { useAtomCallback } from "jotai/utils";
import { activeConversationSidAtom, visitorNameAtom } from "../state";
import { useParams } from "react-router-dom";
import { logger } from "../../datadog/logger";
import { useAtomValue } from "jotai";
import {
  UpdateVisitorPresenceMutation,
  UpdateVisitorPresenceMutationVariables,
} from "../../generated/graphql";
import { admissionTimeAtom } from "./admission/state";
import { useAdmissionFlow } from "../../hooks/useAdmissionFlow";
import { useVideoPlatform } from "../../hooks/useVideoPlatform";

const updateVisitorPresenceMutationGql = gql`
  mutation UpdateVisitorPresence(
    $providerShortId: String!
    $patientShortId: String!
    $appointmentShortId: String
    $visitorUuid: String!
    $visitorName: String!
    $waitingRoomConversationSid: String
    $videoPlatform: String
    $joinRoomMode: String
    $admissionTime: String
  ) {
    updateTwilioVisitorPresence(
      providerShortId: $providerShortId
      patientShortId: $patientShortId
      appointmentShortId: $appointmentShortId
      visitorUuid: $visitorUuid
      visitorName: $visitorName
      waitingRoomConversationSid: $waitingRoomConversationSid
      videoPlatform: $videoPlatform
      joinRoomMode: $joinRoomMode
      admissionTime: $admissionTime
    ) {
      updatedAt
    }
  }
`;

export function useUpdateVisitorPresenceMutation() {
  return useMutation<
    UpdateVisitorPresenceMutation,
    UpdateVisitorPresenceMutationVariables
  >(updateVisitorPresenceMutationGql);
}

export function useUpdateVisitorPresenceCallback() {
  const visitorUuid = useVisitorId();
  const [updateVisitorPresence] = useUpdateVisitorPresenceMutation();
  const [, getVideoPlatform] = useVideoPlatform();
  const [, getAdmissionFlow] = useAdmissionFlow();
  const { providerShortId, patientShortId } = useParams() as {
    providerShortId: string;
    patientShortId: string;
  };
  const visitorName = useAtomValue(visitorNameAtom);
  const { data } = useGetTelehealthSessionInfo(providerShortId, patientShortId);
  const getActiveConversationSid = useAtomCallback(
    useCallback((get) => get(activeConversationSidAtom), []),
  );
  const getAdmissionTime = useAtomCallback(
    useCallback((get) => get(admissionTimeAtom), []),
  );
  const appointmentShortId = data?.telehealthSessionInfo?.appointment?.shortId;

  return useCallback(
    async () => {
      const activeConversationSid = getActiveConversationSid();
      try {
        await updateVisitorPresence({
          variables: {
            providerShortId,
            patientShortId,
            appointmentShortId,
            visitorUuid,
            visitorName,
            joinRoomMode: getAdmissionFlow(),
            admissionTime: getAdmissionTime(),
            ...(activeConversationSid && {
              waitingRoomConversationSid: activeConversationSid,
            }),
            videoPlatform: getVideoPlatform(),
          },
          context: {
            skipAuth: true,
          },
        });
      } catch (e) {
        const error = e as Error;
        logger.error("Unable to update visitor presence", {}, error);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      providerShortId,
      patientShortId,
      appointmentShortId,
      visitorUuid,
      visitorName,
      getVideoPlatform,
      getAdmissionFlow,
    ],
  );
}
